// src/pages/CoursePage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { useStudent } from "../hooks/useStudent";
import AudioPlayerOverlay from "../components/AudioPlayerOverlay";
import { useTranslation } from "react-i18next";
import { translateCourseName } from "../utils/musicTerms";

function subjectEmoji(s) {
  return {Vocal:"🎤",Harmonium:"🎹",Tabla:"🥁",Sitar:"🎸",Flute:"🎵"}[s]||"🎼";
}

function isMobile() {
  return window.innerWidth <= 900;
}

export default function CoursePage() {
  const { id: courseId }   = useParams();
  const { state }          = useLocation();
  const navigate           = useNavigate();
  const batchId            = state?.batchId    || "";
  const isCompleted        = state?.isCompleted|| false;
  const { student }        = useStudent();
  const { t, i18n }        = useTranslation();

  const [course,       setCourse]       = useState(null);
  const [raags,        setRaags]        = useState([]);
  const [unlockedIds,  setUnlockedIds]  = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Desktop accordion state
  const [openRaag,     setOpenRaag]     = useState(null);
  const [openScale,    setOpenScale]    = useState(null);

  // Audio player overlay
  const [playingTrack, setPlayingTrack] = useState(null);

  // Mobile drill-down state
  const [mobile,       setMobile]       = useState(isMobile());
  const [drillRaag,    setDrillRaag]    = useState(null); // selected raag obj
  const [drillScale,   setDrillScale]   = useState(null); // selected scale obj
  const [drillType,    setDrillType]    = useState(null); // 'audio'|'pdf'|'image'|'video'|'common'
  const [drillScales,  setDrillScales]  = useState([]);
  const [drillTracks,  setDrillTracks]  = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    const onResize = () => setMobile(isMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { loadAll(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    try {
      const cSnap = await getDoc(doc(db,"courses",courseId));
      if (cSnap.exists()) setCourse(cSnap.data());

      if (!isCompleted && batchId) {
        const bSnap = await getDoc(doc(db,"batches",batchId));
        if (bSnap.exists())
          setUnlockedIds(bSnap.data().unlockedRaags||[]);
      }

      const rSnap = await getDocs(
        collection(db,"courses",courseId,"raags"));
      setRaags(rSnap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>(a.order||0)-(b.order||0)));
    } catch(e){ console.error(e); }
    setLoading(false);
  }

  const unlocked = id => isCompleted || unlockedIds.includes(id);
  const raagItems = raags.filter(r=>r.type!=="material");
  const matItems  = raags.filter(r=>r.type==="material");
  const uCount    = raags.filter(r=>unlocked(r.id)).length;

  // ── Mobile drill-down: open a raag ──
  async function mobileTapRaag(raag) {
    if (!unlocked(raag.id)) return;
    setDrillRaag(raag);
    setDrillScale(null);
    setDrillType(null);
    setDrillScales([]);
    setDrillTracks([]);
    setDrillLoading(true);
    const [scSnap,tSnap] = await Promise.all([
      getDocs(collection(db,"courses",courseId,"raags",raag.id,"scales")),
      getDocs(collection(db,"courses",courseId,"raags",raag.id,"tracks")),
    ]);
    setDrillScales(scSnap.docs.map(d=>({id:d.id,...d.data()}))
      .sort((a,b)=>(a.name||"").localeCompare(b.name)));
    setDrillTracks(tSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??99999)-(b.order??99999)));
    setDrillLoading(false);
  }

  // ── Mobile drill-down: open a scale ──
  async function mobileTapScale(scale) {
    setDrillScale(scale);
    setDrillLoading(true);
    const snap = await getDocs(
      collection(db,"courses",courseId,"raags",drillRaag.id,"scales",scale.id,"tracks"));
    setDrillTracks(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??99999)-(b.order??99999)));
    setDrillLoading(false);
  }

  // ── Mobile back ──
  function mobileBack() {
    if (drillScale) { setDrillScale(null); return; }
    if (drillType)  { setDrillType(null);  return; }
    if (drillRaag)  { setDrillRaag(null);  return; }
  }

  const courseBanner = (
    <div style={{ background:"linear-gradient(135deg,var(--surface-2),var(--sb-stu-bg))",
                  border:"1px solid var(--border-warm)",
                  borderLeft:"4px solid var(--gold)",
                  borderRadius:"14px",padding:mobile?"14px 16px":"22px 26px",
                  marginBottom:"20px",
                  display:"flex",justifyContent:"space-between",
                  alignItems:"center",flexWrap:"wrap",gap:"12px" }}>
      <div>
        <div style={{ fontSize:".75rem",color:"var(--gold)",fontWeight:"700",
                      marginBottom:"5px",
                      textTransform:"uppercase",letterSpacing:"1.2px" }}>
          {isCompleted ? t("course.completedCourse") : t("course.activeCourse")}
        </div>
        <h1 style={{ fontSize:mobile?"1.05rem":"1.35rem",fontWeight:"800",
                     color:"var(--sb-text-hi)" }}>
          {subjectEmoji(course?.subject)}{" "}
          {translateCourseName(course, i18n.language)}
        </h1>
        {course?.description && (
          <p style={{ color:"var(--text-muted)",fontSize:".85rem",marginTop:"5px" }}>
            {course.description}
          </p>
        )}
      </div>
      {!isCompleted && (
        <div style={{ background:"var(--surface)",border:"1px solid var(--border-warm)",
                      borderTop:"3px solid var(--gold)",
                      borderRadius:"10px",padding:"10px 18px",
                      textAlign:"center",flexShrink:0 }}>
          <div style={{ fontSize:"1.3rem",fontWeight:"800",color:"var(--sb-text-hi)" }}>
            {uCount}/{raags.length}
          </div>
          <div style={{ fontSize:".72rem",color:"var(--gold)",
                        fontWeight:"600",marginTop:"2px" }}>
            {t("course.unlockedLabel")}
          </div>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════
  // MOBILE VIEW
  // ═══════════════════════════════════
  if (mobile) {
    const pageTitle = drillScale
      ? `Scale: ${drillScale.name}`
      : drillRaag
        ? drillRaag.name
        : course
          ? `${subjectEmoji(course.subject)} ${translateCourseName(course, i18n.language)}`
          : "Course";

    return (
      <Layout title={pageTitle} student={student}>
        {loading ? (
          <div className="empty-state"><div className="icon">🎵</div><p>{t("course.loading")}</p></div>
        ) : (
          <>
            {/* Back button */}
            <button onClick={drillRaag ? mobileBack : () => navigate(-1)}
              style={{ display:"flex",alignItems:"center",gap:"8px",
                       background:"none",border:"none",cursor:"pointer",
                       color:"var(--purple-text)",fontWeight:"700",fontSize:".9rem",
                       marginBottom:"14px",padding:"4px 0" }}>
              ← {t("common.back")}
            </button>

            {/* Level 0: Raag list */}
            {!drillRaag && (
              <>
                {courseBanner}
                <div style={{ display:"flex",flexDirection:"column",gap:"10px" }}>
                  {raagItems.map(r => (
                    <MobileTile key={r.id}
                      icon="🎵"
                      title={r.name}
                      subtitle={r.description}
                      locked={!unlocked(r.id)}
                      onClick={() => mobileTapRaag(r)}
                    />
                  ))}
                  {matItems.map(m => (
                    <MobileTile key={m.id}
                      icon="📁"
                      title={m.name}
                      subtitle={m.description}
                      locked={!unlocked(m.id)}
                      onClick={() => unlocked(m.id) && mobileTapRaag(m)}
                    />
                  ))}
                  {raagItems.length === 0 && (
                    <div className="empty-state">
                      <div className="icon">🎵</div>
                      <p>{t("course.noContent")}</p>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Level 1: Content inside a raag */}
            {drillRaag && !drillScale && (
              drillLoading ? (
                <div className="empty-state"><div className="icon">🎵</div><p>{t("course.loading")}</p></div>
              ) : drillRaag.type === "material" ? (
                // Material folder → direct tracks
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">📁 {drillRaag.name}</span>
                    <span className="badge badge-purple">
                      {drillTracks.length}
                    </span>
                  </div>
                  {drillTracks.length === 0
                    ? <p className="muted">{t("library.noFiles")}</p>
                    : drillTracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={setPlayingTrack}/>)
                  }
                </div>
              ) : (
                // Raag → scales or common tracks
                <div style={{ display:"flex",flexDirection:"column",gap:"10px" }}>
                  {drillScales.map(sc => (
                    <MobileTile key={sc.id}
                      icon="🎼"
                      title={`${t("course.scalePrefix")} ${sc.name}`}
                      color="#0D9488"
                      onClick={() => mobileTapScale(sc)}
                    />
                  ))}
                  {drillTracks.length > 0 && (
                    <div className="card" style={{ marginTop:"4px" }}>
                      <div className="card-header">
                        <span className="card-title">📎 {t("course.commonAllScales")}</span>
                        <span className="badge badge-purple">
                          {drillTracks.length}
                        </span>
                      </div>
                      {drillTracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={setPlayingTrack}/>)}
                    </div>
                  )}
                  {drillScales.length===0 && drillTracks.length===0 && (
                    <div className="empty-state">
                      <div className="icon">📭</div>
                      <p>{t("course.noContent")}</p>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Level 2: Tracks inside a scale */}
            {drillScale && (
              drillLoading ? (
                <div className="empty-state"><div className="icon">🎼</div><p>{t("course.loading")}</p></div>
              ) : (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">🎼 {t("course.scalePrefix")} {drillScale.name}</span>
                    <span className="badge badge-purple">
                      {drillTracks.length}
                    </span>
                  </div>
                  {drillTracks.length === 0
                    ? <p className="muted">{t("course.noAudio")}</p>
                    : drillTracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={setPlayingTrack}/>)
                  }
                </div>
              )
            )}
          </>
        )}
        <AudioPlayerOverlay track={playingTrack} onClose={()=>setPlayingTrack(null)}/>
      </Layout>
    );
  }

  // ═══════════════════════════════════
  // DESKTOP VIEW (accordion, unchanged)
  // ═══════════════════════════════════
  return (
    <Layout title={course
      ? `${subjectEmoji(course.subject)} ${translateCourseName(course, i18n.language)}`
      : "Course"} student={student}>

      {loading ? (
        <div className="empty-state"><div className="icon">🎵</div><p>{t("course.loading")}</p></div>
      ) : (
        <>
          <button onClick={() => navigate(-1)}
            style={{ display:"inline-flex",alignItems:"center",gap:"6px",
                     background:"none",border:"none",cursor:"pointer",
                     color:"var(--purple-text)",fontWeight:"700",fontSize:".9rem",
                     marginBottom:"16px",padding:"4px 0" }}>
            ← {t("common.back")}
          </button>
          {courseBanner}

          <div className="course-grid"
               style={{ display:"grid",
                         gridTemplateColumns:"1fr 320px",gap:"20px",
                         alignItems:"start" }}>

            {/* Main: Raags */}
            <div>
              {raagItems.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">🎵 Raags</span>
                    <span className="badge badge-purple">
                      {raagItems.length} raag{raagItems.length!==1?"s":""}
                    </span>
                  </div>
                  {raagItems.map(r => (
                    <RaagAccordion key={r.id} raag={r}
                      courseId={courseId}
                      unlocked={unlocked(r.id)}
                      isOpen={openRaag===r.id}
                      openScale={openScale}
                      setOpenScale={setOpenScale}
                      onPlay={setPlayingTrack}
                      onToggle={()=>setOpenRaag(
                        openRaag===r.id?null:r.id)}
                    />
                  ))}
                </div>
              )}
              {raagItems.length === 0 && (
                <div className="empty-state">
                  <div className="icon">🎵</div>
                  <p>{t("course.noRaags")}</p>
                </div>
              )}
            </div>

            {/* Sidebar: Course Material */}
            <div>
              {matItems.map(m=>(
                <div className="card" key={m.id}>
                  <div className="card-header" style={{ marginBottom:"12px" }}>
                    <span className="card-title">📁 {m.name}</span>
                    {!unlocked(m.id) && (
                      <span className="badge badge-gray">{t("course.locked")}</span>
                    )}
                  </div>
                  {!unlocked(m.id) ? (
                    <div style={{ padding:"16px 0",textAlign:"center",
                                  color:"var(--text-muted)",fontSize:".85rem" }}>
                      {t("course.locked")}
                    </div>
                  ) : (
                    <MaterialContent courseId={courseId} folderId={m.id} onPlay={setPlayingTrack}/>
                  )}
                </div>
              ))}

              {/* Quick info */}
              <div className="card" style={{ background:"var(--surface-2)",
                                              border:"1px solid var(--border-warm)" }}>
                <div style={{ fontSize:".82rem",color:"var(--text-label)",
                              lineHeight:1.8 }}>
                  <div style={{ fontWeight:"700",color:"var(--purple-text)",
                                marginBottom:"8px" }}>
                    {t("course.howToAccess")}
                  </div>
                  <div>{t("course.unlockedClick")}</div>
                  <div>{t("course.lockedContact")}</div>
                  <div>{t("course.audioNote")}</div>
                  <div>{t("course.pdfNote")}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <AudioPlayerOverlay track={playingTrack} onClose={()=>setPlayingTrack(null)}/>
    </Layout>
  );
}

// ── Mobile Tile ───────────────────────────────────────────────────────────────
function MobileTile({ icon, title, subtitle, locked, onClick, color }) {
  const { t } = useTranslation();
  return (
    <div onClick={locked ? undefined : onClick}
      style={{
        display:"flex", alignItems:"center", gap:"14px",
        background:"var(--surface)", borderRadius:"12px",
        padding:"14px 16px",
        border: locked ? "1.5px solid var(--border)" : `1.5px solid ${color||"var(--border-warm)"}`,
        cursor: locked ? "default" : "pointer",
        boxShadow:"0 1px 4px rgba(0,0,0,.06)",
        opacity: locked ? .55 : 1,
      }}>
      <div style={{ width:"42px",height:"42px",borderRadius:"10px",
                    background: locked ? "var(--input-bg)" : (color ? `${color}18` : "var(--sb-stu-bg)"),
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:"1.3rem",flexShrink:0 }}>
        {locked ? "🔒" : icon}
      </div>
      <div style={{ flex:1,minWidth:0 }}>
        <div style={{ fontWeight:"700",fontSize:".95rem",
                      color: locked ? "var(--text-muted)" : "var(--text)",
                      whiteSpace:"nowrap",overflow:"hidden",
                      textOverflow:"ellipsis" }}>
          {title}
        </div>
        {subtitle && (
          <div className="muted" style={{ marginTop:"2px",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
            {subtitle}
          </div>
        )}
        {locked && (
          <div style={{ fontSize:".75rem",color:"var(--text-muted)",marginTop:"2px" }}>
            {t("course.contactTeacher")}
          </div>
        )}
      </div>
      {!locked && (
        <span style={{ color: color||"var(--purple)",fontSize:"1.3rem",flexShrink:0 }}>›</span>
      )}
    </div>
  );
}

// ── Raag Accordion (desktop) ──────────────────────────────────────────────────
function RaagAccordion({ raag, courseId, unlocked,
                         isOpen, onToggle, openScale, setOpenScale, onPlay }) {
  const { t } = useTranslation();
  const [scales,       setScales]       = useState([]);
  const [commonTracks, setCommonTracks] = useState([]);
  const [loaded,       setLoaded]       = useState(false);

  async function load() {
    if (loaded) return;
    const [scSnap,tSnap] = await Promise.all([
      getDocs(collection(db,"courses",courseId,"raags",raag.id,"scales")),
      getDocs(collection(db,"courses",courseId,"raags",raag.id,"tracks")),
    ]);
    setScales(scSnap.docs.map(d=>({id:d.id,...d.data()}))
      .sort((a,b)=>(a.name||"").localeCompare(b.name)));
    setCommonTracks(tSnap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??99999)-(b.order??99999)));
    setLoaded(true);
  }

  function toggle() {
    if (!unlocked) return;
    if (!isOpen) load();
    onToggle();
  }

  return (
    <div className="accordion-item">
      <div className={`accordion-header ${unlocked?"unlocked":"locked"}`}
        onClick={toggle}
        style={{ cursor: unlocked?"pointer":"default" }}>
        <span style={{ fontSize:"1.3rem" }}>🎵</span>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:"700",
                        color: unlocked?"var(--text)":"var(--text-muted)" }}>
            {raag.name}
          </div>
          {raag.description && (
            <div className="muted">{raag.description}</div>
          )}
        </div>
        {unlocked ? (
          <span style={{ color:"var(--purple-text)",fontSize:"1.2rem",
                         transform:isOpen?"rotate(90deg)":"none",
                         transition:"transform .2s",display:"inline-block" }}>
            ›
          </span>
        ) : (
          <span className="badge badge-gray">{t("course.locked")}</span>
        )}
      </div>

      {isOpen && unlocked && (
        <div className="accordion-body">
          {scales.map(sc=>(
            <ScaleAccordion key={sc.id} scale={sc}
              courseId={courseId} raagId={raag.id}
              isOpen={openScale===sc.id}
              onPlay={onPlay}
              onToggle={()=>setOpenScale(
                openScale===sc.id?null:sc.id)}
            />
          ))}

          {commonTracks.length > 0 && (
            <div style={{ marginTop:"14px" }}>
              <div style={{ fontSize:".8rem",fontWeight:"700",
                            color:"var(--purple-text)",marginBottom:"8px" }}>
                <span style={{ background:"var(--purple)",color:"white",
                               padding:"2px 10px",borderRadius:"99px",
                               fontSize:".73rem" }}>
                  {t("course.commonAllScales")}
                </span>
              </div>
              {commonTracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={onPlay}/>)}
            </div>
          )}

          {scales.length===0 && commonTracks.length===0 && (
            <p className="muted" style={{ padding:"8px 0" }}>
              {t("course.noContent")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Scale Accordion (desktop) ─────────────────────────────────────────────────
function ScaleAccordion({ scale, courseId, raagId, isOpen, onToggle, onPlay }) {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState([]);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    if (loaded) return;
    const snap = await getDocs(
      collection(db,"courses",courseId,"raags",raagId,"scales",scale.id,"tracks"));
    setTracks(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??99999)-(b.order??99999)));
    setLoaded(true);
  }

  return (
    <div className="scale-accordion">
      <div className="scale-header"
        onClick={()=>{ if(!isOpen)load(); onToggle(); }}
        style={{ cursor:"pointer" }}>
        <span style={{ background:"#0D9488",color:"white",fontSize:".78rem",
                       fontWeight:"700",padding:"2px 12px",
                       borderRadius:"99px" }}>
          {t("course.scalePrefix")} {scale.name}
        </span>
        <span style={{ flex:1 }}/>
        <span style={{ color:"#0D9488",
                       transform:isOpen?"rotate(90deg)":"none",
                       transition:"transform .2s",display:"inline-block" }}>
          ›
        </span>
      </div>
      {isOpen && (
        <div className="scale-body">
          {tracks.length===0
            ? <p className="muted" style={{ fontSize:".82rem" }}>
                {t("course.noAudio")}
              </p>
            : tracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={onPlay}/>)
          }
        </div>
      )}
    </div>
  );
}

// ── Material Content (desktop) ────────────────────────────────────────────────
function MaterialContent({ courseId, folderId, onPlay }) {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState([]);
  useEffect(()=>{
    getDocs(collection(db,"courses",courseId,"raags",folderId,"tracks"))
      .then(snap=>setTracks(snap.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>(a.order??99999)-(b.order??99999))));
  },[courseId,folderId]);
  if (tracks.length===0)
    return <p className="muted" style={{ fontSize:".82rem" }}>{t("library.noFiles")}</p>;
  return <>{tracks.map(tr=><TrackRow key={tr.id} track={tr} onPlay={onPlay}/>)}</>;
}

// ── Track Row (shared) ────────────────────────────────────────────────────────
function TrackRow({ track, onPlay }) {
  const { t } = useTranslation();
  const trackUrl  = track.url || track.fileUrl || track.audioUrl || "";
  const trackType = (track.type || "").toLowerCase();
  const isAudio   = trackType === "audio" ||
                    (!trackType && trackUrl.includes("firebasestorage.googleapis.com")) ||
                    (!trackType && /\.(mp3|m4a|wav|ogg|aac)(\?|$)/i.test(trackUrl));

  const meta = {
    audio: {lbl:"AUDIO",cls:"badge-audio",icon:"🎵",action:t("common.play")},
    pdf:   {lbl:"PDF",  cls:"badge-pdf",  icon:"📄",action:t("common.openFile")},
    image: {lbl:"IMAGE",cls:"badge-image",icon:"🖼️",action:t("common.openFile")},
    video: {lbl:"VIDEO",cls:"badge-video",icon:"🎬",action:t("common.watch")},
  }[trackType] || {lbl:"FILE",cls:"badge-gray",icon:"📎",action:t("common.open")};

  return (
    <div className="track-row">
      <span style={{ fontSize:"1.2rem",flexShrink:0 }}>{meta.icon}</span>
      <div style={{ flex:1,minWidth:0 }}>
        <div style={{ fontWeight:"600",fontSize:".9rem" }}>{track.title}</div>
        {track.description && (
          <div className="muted">{track.description}</div>
        )}
      </div>
      <span className={`badge ${meta.cls}`} style={{ flexShrink:0 }}>{meta.lbl}</span>
      {isAudio ? (
        <button
          className="btn btn-primary btn-sm"
          style={{ flexShrink:0 }}
          onClick={() => onPlay && onPlay(track)}>
          {t("common.play")}
        </button>
      ) : (
        <a href={trackUrl} target="_blank" rel="noreferrer"
          className="btn btn-primary btn-sm"
          style={{ flexShrink:0 }}>
          {meta.action}
        </a>
      )}
    </div>
  );
}
